/*
 * KM2 Mega-Menu — live in-stock brand client.
 *
 * Plan: 20260621-190500 (Task 2). Data layer ONLY — returns plain data, never touches
 * the DOM (rendering + XSS-safe textContent injection live in the controller, Task 3).
 *
 * Why aggregation, not a facet: the Storefront `search()` connection on this store does
 * NOT expose a vendor productFilter (validated against the live 2026-04 API — only Price
 * is configured). So top brands are derived by querying
 *   products(first:250, query:"available_for_sale:true AND <audience>")
 * and counting vendors client-side. The availability filter is SERVER-side, so the
 * response contains only in-stock products' vendors — sold-out vendors are simply absent
 * (never rendered). Catalogue is ~375 products, so each context is 1–2 pages.
 *
 * Public API (attached to window.KM2Brands):
 *   config: { shop, apiVersion, token }   // set by Liquid (Task 3) or by a test
 *   fetchTopBrands(context) -> Promise<Array<{ vendor: string, count: number }>>
 *     context = {
 *       key:       string,    // stable cache key (e.g. 'men' | 'women' | 'kids' | 'brands')
 *       query:     string,    // Storefront search query (already includes available_for_sale)
 *       cap?:      number,    // max brands returned (default 12; <=0 = no cap)
 *       denyList?: string[],  // vendor names to exclude (case-insensitive) — e.g. house labels
 *     }
 *   Results are ranked by in-stock product count (desc), ties broken alphabetically,
 *   deny-listed names removed, then capped. The RAW per-vendor aggregation is cached in
 *   sessionStorage per `key`, so a second open of the same context performs no 2nd request;
 *   deny-list/cap are re-applied on every call (cheap, and lets contexts differ safely).
 */
(function () {
  if (window.KM2Brands) return; // idempotent — single client per page

  var CACHE_PREFIX = 'km2brands:v1:';
  var PAGE_SIZE = 250; // fetchTopBrands aggregation page (whole-catalogue vendor scan)
  var RAIL_PAGE_SIZE = 50; // homepage-rail pager default: a modest page per scroll, NOT a 250-preload
  var PAGE_CAP = 8; // safety bound (8 * 250 = 2000 products) — catalogue is far smaller
  var DEFAULT_CAP = 12;

  var KM2Brands = {
    config: { shop: '', apiVersion: '2026-04', token: '' },
    fetchTopBrands: fetchTopBrands,         // lightweight ranking index: [{vendor, count}] only
    createProductPager: createProductPager, // TRUE lazy: ONE Storefront product page per nextPage() call
    // exposed for tests / cache busting
    _clearCache: function (key) {
      try {
        sessionStorage.removeItem(CACHE_PREFIX + key);
      } catch (e) {
        /* sessionStorage unavailable — ignore */
      }
    },
  };

  var GQL =
    'query TopBrands($q: String!, $cursor: String) {' +
    '  products(first: ' +
    PAGE_SIZE +
    ', query: $q, after: $cursor) {' +
    '    pageInfo { hasNextPage endCursor }' +
    '    edges { node { vendor } }' +
    '  }' +
    '}';

  // One Storefront product page per call (cursor held in the pager closure). Brand rails use
  // GQL_PAGE (products() in BEST_SELLING in-stock order); EBC uses GQL_COLLECTION_PAGE
  // (collection.products with a BACKEND available:true filter). Each returns { nodes, hasMore }
  // for THIS page only — never the whole catalogue. This is the pure-lazy source that REPLACED
  // the old fetch-all rep/collection paths (which preloaded + tripped the REST 429 rate limit).
  // `first` is a VARIABLE (not baked in) so each pager picks its own page granularity: the rails
  // pass a SMALL size (genuinely one modest page per scroll — nothing preloaded), while a caller
  // that wants a bulk page can still pass up to PAGE_SIZE. Defaulted to RAIL_PAGE_SIZE per call.
  // $sort (ProductSortKeys) + $reverse let a caller pick the order (rails default BEST_SELLING asc).
  var GQL_PAGE =
    'query Page($q: String, $cursor: String, $sort: ProductSortKeys, $first: Int, $reverse: Boolean) {' +
    '  products(first: $first, query: $q, after: $cursor, sortKey: $sort, reverse: $reverse) {' +
    '    pageInfo { hasNextPage endCursor }' +
    '    edges { node { vendor title handle productType tags availableForSale' +
    '      featuredImage { url } images(first: 4) { edges { node { url } } }' +
    // first:20 + `id` + `title` mirror GQL_COLLECTION_PAGE below (plan 20260901-050844 T4).
    // WHY 20, not 2: the card contract now carries a variant `title`, which is what a size picker
    // renders — and `title` is useless if the window is too narrow to contain the sizes. The live
    // catalogue's worst case is 9 variants (olivia-clay), so 20 covers it with headroom.
    //
    // This is NOT payload-only (round-1 f5). Four consumers answer availability from the FETCHED
    // subset, so widening the window moves them: quickAddButton's `available.length > 1` branch,
    // ebc-spotlight's ebcAvailable -> rail membership, kmf-filters' productAvailable fallback
    // (sold-out badge, in-stock filter, sold-out-last sort), and km2-brand-tiles' isAvailable.
    // The resulting delta is an AUTHORISED, MEASURED exception: of 2,863 active products
    // (144 multi-variant) exactly 2 are affected — see the plan's T4/AC31. Re-measure before T5.
    '      variants(first: 20) { edges { node { id title availableForSale price { amount } compareAtPrice { amount } } } } } }' +
    '  }' +
    '}';
  // $filters / $sortKey / $reverse are caller-supplied PARAMETERIZED variables (A03: filter values are
  // NEVER string-concatenated into the query body). createProductPager DEFAULTS $filters to
  // [{available:true}] when a caller omits it, so the EBC rail keeps its in-stock-only guarantee
  // (BACKEND returns only in-stock products — OOS never fetched). The collection grid (kmf-filters.js)
  // passes an explicit filter set built from the sidebar facets + an explicit sortKey.
  var GQL_COLLECTION_PAGE =
    'query ColPage($handle: String!, $cursor: String, $first: Int, $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {' +
    '  collection(handle: $handle) {' +
    '    products(first: $first, after: $cursor, filters: $filters, sortKey: $sortKey, reverse: $reverse) {' +
    '      pageInfo { hasNextPage endCursor }' +
    '      edges { node { vendor title handle productType tags availableForSale' +
    '        featuredImage { url } images(first: 4) { edges { node { url } } }' +
    // first:20 (not 2) so the fetched window can hold a product's real size range — the card
    // contract now carries a variant `title`, and a size picker cannot render sizes the query
    // never returned. `id` is the variant GID the EBC card needs to POST /cart/add.js for a
    // direct single-variant add; `title` is the size label itself. See GQL_PAGE above for the
    // four availability consumers this widening moves, and the measured 2-product exception.
    '        variants(first: 20) { edges { node { id title availableForSale price { amount } compareAtPrice { amount } } } } } }' +
    '    }' +
    '  }' +
    '}';

  function endpoint() {
    return 'https://' + KM2Brands.config.shop + '/api/' + KM2Brands.config.apiVersion + '/graphql.json';
  }

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, agg) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(agg));
    } catch (e) {
      /* quota / disabled — non-fatal, we just refetch next time */
    }
  }

  // Shared Storefront GraphQL transport (DRY): POST `query` with `baseVars`, follow the
  // products() cursor until hasNextPage is false (or PAGE_CAP), invoking onPage(edges) once
  // per page. Used by fetchAggregation (the fetchTopBrands ranking index). The homepage rails
  // do NOT use this drain-all transport — they page lazily via createProductPager instead.
  function gqlPaginate(query, baseVars, onPage, pick) {
    var cursor = null;
    var page = 0;
    function step() {
      page++;
      var vars = { cursor: cursor };
      for (var k in baseVars) vars[k] = baseVars[k];
      return fetch(endpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': KM2Brands.config.token,
        },
        body: JSON.stringify({ query: query, variables: vars }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('storefront HTTP ' + res.status);
          return res.json();
        })
        .then(function (json) {
          var data = json && json.data;
          // `pick` selects the paginated connection (default: top-level products(); EBC uses
          // collection(handle).products). Both share the same {pageInfo, edges} cursor shape.
          var conn = pick ? pick(data) : (data && data.products);
          // An error reply (GraphQL `errors`, no data) is a FAILURE, not "no brands in stock":
          // resolving it empty culled every curated brand and cached the void (audit 2026-10-05 #4).
          // Rejecting hands it to the callers' existing fail-open paths. A later page failing
          // keeps what the earlier pages gathered.
          if (!conn) {
            if (page === 1) throw new Error('storefront returned no data');
            return;
          }
          onPage(conn.edges || []);
          var pi = conn.pageInfo || {};
          if (pi.hasNextPage && pi.endCursor && page < PAGE_CAP) {
            cursor = pi.endCursor;
            return step();
          }
        });
    }
    return step();
  }

  // Fetch all pages for `query` and return a raw [{vendor, count}] aggregation (unfiltered).
  function fetchAggregation(query) {
    var counts = Object.create(null);
    return gqlPaginate(GQL, { q: query }, function (edges) {
      for (var i = 0; i < edges.length; i++) {
        var v = edges[i] && edges[i].node && edges[i].node.vendor;
        if (!v) continue;
        counts[v] = (counts[v] || 0) + 1;
      }
    }).then(function () {
      return Object.keys(counts).map(function (vendor) {
        return { vendor: vendor, count: counts[vendor] };
      });
    });
  }

  // Apply deny-list (case-insensitive), rank by count desc then name asc, cap.
  function shape(agg, denyList, cap) {
    var deny = Object.create(null);
    (denyList || []).forEach(function (name) {
      deny[String(name).toLowerCase()] = true;
    });
    var out = agg.filter(function (b) {
      return !deny[String(b.vendor).toLowerCase()];
    });
    out.sort(function (a, b) {
      if (b.count !== a.count) return b.count - a.count;
      return a.vendor.localeCompare(b.vendor);
    });
    var limit = typeof cap === 'number' ? cap : DEFAULT_CAP;
    if (limit > 0 && out.length > limit) out = out.slice(0, limit);
    return out;
  }

  function fetchTopBrands(context) {
    context = context || {};
    var key = context.key || context.query || 'default';
    var cached = readCache(key);
    if (cached) {
      return Promise.resolve(shape(cached, context.denyList, context.cap));
    }
    return fetchAggregation(context.query || '').then(function (agg) {
      writeCache(key, agg);
      return shape(agg, context.denyList, context.cap);
    });
  }

  // createProductPager — TRUE lazy: nextPage() fetches the NEXT page only (cursor in the closure).
  // Brand rails omit collectionHandle → products() with a BEST_SELLING in-stock query string.
  // EBC passes collectionHandle → collection.products with a backend available:true filter.
  // Either way nextPage() resolves { nodes, hasMore } for THIS page; it NEVER drains all pages.
  function createProductPager(opts) {
    opts = opts || {};
    var cursor = null;
    var done = false;
    function postPage() {
      var isCol = !!opts.collectionHandle;
      var query = isCol ? GQL_COLLECTION_PAGE : GQL_PAGE;
      var first = opts.pageSize || RAIL_PAGE_SIZE;
      // Collection path: filters DEFAULT to [{available:true}] (EBC back-compat) but a caller may pass
      // an explicit set (the grid). sortKey/reverse are pass-through (null sortKey = collection default).
      var vars = isCol
        ? {
            handle: opts.collectionHandle, cursor: cursor, first: first,
            filters: opts.filters !== undefined ? opts.filters : [{ available: true }],
            sortKey: opts.sortKey || null, reverse: !!opts.reverse,
          }
        : {
            // query != null (not just truthy): the collection grid's /search-browse path passes an
            // explicit '' to mean "all products incl. sold-out" (in-stock filter off). Only an
            // UNDEFINED query falls back to the in-stock default the rails rely on.
            q: opts.query != null ? opts.query : 'available_for_sale:true',
            cursor: cursor, sort: opts.sortKey || 'BEST_SELLING', first: first, reverse: !!opts.reverse,
          };
      return fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': KM2Brands.config.token },
        body: JSON.stringify({ query: query, variables: vars }),
      }).then(function (res) { return res.json(); }).then(function (json) {
        var data = json && json.data;
        var conn = isCol ? (data && data.collection && data.collection.products) : (data && data.products);
        // A null connection means a GraphQL error / missing collection (NOT an empty result — that
        // still returns a connection with edges:[]). Flag it so callers can restore a fallback grid.
        if (!conn) { done = true; return { nodes: [], hasMore: false, error: true }; }
        var nodes = (conn.edges || []).map(function (e) { return e.node; });
        var pi = conn.pageInfo || {};
        cursor = pi.endCursor || cursor;
        done = !pi.hasNextPage;
        return { nodes: nodes, hasMore: !!pi.hasNextPage };
      }).catch(function () { done = true; return { nodes: [], hasMore: false, error: true }; });
    }
    return {
      nextPage: function () { return done ? Promise.resolve({ nodes: [], hasMore: false }) : postPage(); },
    };
  }

  // Config is injected by the theme (header.liquid) as a plain object BEFORE this
  // deferred module runs, so it can't assume window.KM2Brands exists yet. Merge it in.
  if (window.KM2BrandsConfig) {
    KM2Brands.config.shop = window.KM2BrandsConfig.shop || KM2Brands.config.shop;
    KM2Brands.config.apiVersion = window.KM2BrandsConfig.apiVersion || KM2Brands.config.apiVersion;
    KM2Brands.config.token = window.KM2BrandsConfig.token || KM2Brands.config.token;
  }

  window.KM2Brands = KM2Brands;
})();

/*
 * KM2 Mega-Menu — controller (Task 3).
 *
 * DOM layer ONLY — consumes the data-layer client above (window.KM2Brands). On first
 * open of an audience panel (hover / focus / <details> toggle) it hydrates the "Top
 * Brands" column: pinned heroes are already server-rendered (optimistic first paint),
 * then once live data lands it (a) culls any pinned hero that is fully out-of-stock
 * (absent from the in-stock vendor set) and (b) appends auto-fill brands up to a
 * display cap. Every brand name is injected via textContent — never innerHTML (XSS).
 *
 * Contract with snippets/mega-menu-panel.liquid:
 *   [data-km2-brands][data-km2-context][data-km2-query]  — the brand column
 *   [data-km2-brand-list]                                — the <ul> to hydrate
 *   [data-km2-pinned="Vendor Name"]                      — a pinned hero <li>
 */
(function () {
  if (window.KM2MegaMenu) return; // idempotent

  var DISPLAY_CAP = 30; // total brands shown (pinned heroes + auto-fill); list scrolls past ~2 rows
  // House / store-owned labels that are never real "brands" to surface. Kept minimal and
  // case-insensitive. Curated kids houses (Disney/Marvel/Air-Val) are intentionally NOT here.
  // EBC has its own menu (D7, PD7): ONE list, used by the audience deny-list AND the Brands
  // directory's More A–Z, so the two can never disagree about which vendor is EBC.
  var EBC_VENDORS = ['ebc brand', 'ebc'];
  var BASE_DENY = ['km2fragrances.com', 'km2 fragrances', 'km2'].concat(EBC_VENDORS);

  function denyFor(/* context */) {
    return BASE_DENY.slice();
  }

  function searchHref(name) {
    return '/search?q=' + encodeURIComponent(name) + '&options[prefix]=last';
  }

  // slugify: the SINGLE shared contract — MUST stay byte-for-byte identical to
  // ShopifyProductManager/src/scripts/create-brand-collections.ts slugify(), which generated the
  // real collection handles on prod. Diverge by one char and the brand link 404s.
  function slugify(name) {
    return String(name)
      .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents: Hermès -> hermes
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')                       // any run of non-alphanumerics -> single hyphen
      .replace(/^-+|-+$/g, '');                          // trim leading/trailing hyphens
  }

  // Canonical brand link: the per-brand /collections/<slug> page (replaces the old /search?q=).
  // Falls back to search when the slug is empty (e.g. an all-punctuation vendor with no handle).
  function brandHref(name) {
    var slug = slugify(name);
    return slug ? '/collections/' + slug : searchHref(name);
  }

  // The visible brand name of a brand <li> (anchor text, with the pinned attr as a fallback).
  function brandName(li) {
    var a = li.querySelector('a');
    return ((a && a.textContent) || li.getAttribute('data-km2-pinned') || '').trim();
  }

  // Reorder a brand list's <li> children alphabetically ASC (A–Z, case-insensitive via localeCompare).
  // Used by EVERY menu's brand section so the curated pinned/featured tier and the count-ranked
  // auto-fill tier render as one uniform A–Z list (user request: all brand sections sorted A–Z).
  // Re-appends existing nodes (a move, not a clone) so anchors/attributes/listeners are preserved.
  function sortBrandList(listEl) {
    if (!listEl) return;
    var items = Array.prototype.slice.call(listEl.querySelectorAll('li.km2-mega__brand'));
    items.sort(function (a, b) {
      return brandName(a).localeCompare(brandName(b));
    });
    items.forEach(function (li) {
      listEl.appendChild(li);
    });
  }

  // Build one brand <li> with the vendor name set as TEXT (never parsed as HTML).
  function makeBrandLi(name) {
    var li = document.createElement('li');
    li.className = 'km2-mega__brand';
    var a = document.createElement('a');
    a.href = brandHref(name);
    a.textContent = name; // XSS-safe — a vendor like "<img onerror=…>" renders as literal text
    li.appendChild(a);
    return li;
  }

  // Reconcile the server-rendered pinned heroes against the live in-stock vendor set,
  // then top up to DISPLAY_CAP with auto-fill brands. `brands` is ranked desc by count.
  function reconcile(listEl, brands) {
    var inStock = Object.create(null);
    brands.forEach(function (b) {
      inStock[String(b.vendor).toLowerCase()] = true;
    });

    // 1) Cull pinned heroes that are fully OOS (absent from the in-stock set).
    var kept = Object.create(null);
    var pinned = listEl.querySelectorAll('[data-km2-pinned]');
    for (var i = 0; i < pinned.length; i++) {
      var name = (pinned[i].getAttribute('data-km2-pinned') || '').trim();
      if (!name) continue;
      if (inStock[name.toLowerCase()]) {
        kept[name.toLowerCase()] = true;
        // Rewrite the server-rendered /search?q= href to the canonical /collections/<slug>.
        // Liquid can't reproduce the NFD slugify, so the snippet ships a search href as the no-JS
        // fallback and we upgrade it here — one slugify source of truth (brandHref).
        var keepLink = pinned[i].querySelector('a');
        if (keepLink) keepLink.setAttribute('href', brandHref(name));
      } else if (pinned[i].parentNode) {
        pinned[i].parentNode.removeChild(pinned[i]); // sold-out = absent, not struck-through
      }
    }

    // 2) Append auto-fill brands not already pinned, up to the display cap.
    var count = listEl.querySelectorAll('li').length;
    for (var j = 0; j < brands.length && count < DISPLAY_CAP; j++) {
      var v = String(brands[j].vendor);
      if (kept[v.toLowerCase()]) continue;
      listEl.appendChild(makeBrandLi(v));
      count++;
    }
    // Final display order is alphabetical ASC across BOTH tiers (kept pinned heroes + auto-fill):
    // selection stays popularity-ranked (which brands), order is A–Z (how they're listed).
    sortBrandList(listEl);
    listEl.setAttribute('data-km2-hydrated-list', '1');
  }

  // ---- Smart-hide: hide category links whose collection is empty / missing ----
  // ONE batched Storefront query per panel open (aliased collections), cached per audience.
  // Fail-OPEN: on any error or missing data, leave the server-rendered links visible.
  var AVAIL_PREFIX = 'km2avail:v2:'; // v2: stale-while-revalidate + {map,ts} format; v1 verdicts had no expiry

  function buildAvailQuery(handles) {
    var parts = handles.map(function (h, i) {
      return (
        'c' + i + ': collection(handle: ' + JSON.stringify(h) + ') ' +
        '{ products(first: 1, filters: [{ available: true }]) { nodes { id } } }'
      );
    });
    return '{ ' + parts.join(' ') + ' }';
  }

  function availEndpoint() {
    var c = window.KM2Brands.config;
    return 'https://' + c.shop + '/api/' + c.apiVersion + '/graphql.json';
  }

  function fetchAvailability(handles) {
    return fetch(availEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.KM2Brands.config.token,
      },
      body: JSON.stringify({ query: buildAvailQuery(handles) }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        var data = json && json.data;
        if (!data) return null; // GraphQL error -> fail-open
        var map = Object.create(null);
        handles.forEach(function (h, i) {
          // Shopify answers EVERY alias: null = no such collection, nodes [] = nothing in stock.
          // An alias missing from the reply altogether is a malformed answer — leave it unknown
          // (undefined) so callers fail open, never read it as "missing" (audit 2026-10-05).
          if (!(('c' + i) in data)) return;
          var node = data['c' + i];
          map[h] = !!(node && node.products && node.products.nodes && node.products.nodes.length);
        });
        return map;
      });
  }

  function applyHide(panelEl, availMap) {
    var links = panelEl.querySelectorAll('[data-km2-collection]');
    for (var i = 0; i < links.length; i++) {
      var h = links[i].getAttribute('data-km2-collection');
      var li = links[i].closest('li') || links[i];
      // Set the state EXPLICITLY every apply (not just additively hide): a fresh availability map
      // must be able to UN-hide a category that a stale verdict had hidden but is now back in stock.
      li.style.display = availMap[h] === false ? 'none' : '';
    }
    // Show/hide each section to match its links: hidden only when ALL its items are hidden.
    var sections = panelEl.querySelectorAll('[data-km2-section]');
    for (var s = 0; s < sections.length; s++) {
      var items = sections[s].querySelectorAll('.km2-mega__links li');
      var anyVisible = false;
      for (var j = 0; j < items.length; j++) {
        if (items[j].style.display !== 'none') {
          anyVisible = true;
          break;
        }
      }
      sections[s].style.display = items.length && !anyVisible ? 'none' : '';
    }
    // Group level (EBC, PD6): a [data-km2-group] hides when ALL its sections are hidden, and so
    // does its tab. Set explicitly every apply so a restocked group comes back.
    var groups = panelEl.querySelectorAll('[data-km2-group]');
    for (var g = 0; g < groups.length; g++) {
      var groupSections = groups[g].querySelectorAll('[data-km2-section]');
      var groupVisible = false;
      for (var k = 0; k < groupSections.length; k++) {
        if (groupSections[k].style.display !== 'none') {
          groupVisible = true;
          break;
        }
      }
      var empty = groupSections.length > 0 && !groupVisible;
      groups[g].style.display = empty ? 'none' : '';
      var tab = panelEl.querySelector('[data-km2-tab="' + groups[g].getAttribute('data-km2-group') + '"]');
      if (tab) tab.hidden = empty;
    }
    // Runs after the cached paint AND the fresh map: an emptied selected tab never leaves a blank pane.
    selectFirstVisibleTab(panelEl);
  }

  // ---- Side tabs (EBC panel, T5): one visible pane, ARIA tabs pattern ----
  function selectTab(panelEl, tab, focus) {
    var tabs = panelEl.querySelectorAll('[data-km2-tab]');
    for (var i = 0; i < tabs.length; i++) {
      var on = tabs[i] === tab;
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].tabIndex = on ? 0 : -1;
      var pane = panelEl.querySelector('#' + tabs[i].getAttribute('aria-controls'));
      if (pane) pane.hidden = !on;
    }
    // The side column's bestsellers follow the selected tab (owner 2026-10-04).
    var key = tab && tab.getAttribute('data-km2-tab');
    var picks = panelEl.querySelectorAll('[data-km2-ebc-picks]');
    for (var j = 0; j < picks.length; j++) picks[j].hidden = picks[j].getAttribute('data-km2-ebc-picks') !== key;
    if (key) fillPicks(panelEl, key);
    if (focus && tab && typeof tab.focus === 'function') tab.focus();
  }

  // ---- EBC bestsellers (side column): up to PICKS_CAP in-stock EBC products per group ----
  // Reuses the shared Storefront pager (createProductPager), the product model (adapter + the ONE
  // price rule + imageUrl) and KM2ProductCard.money. Once per group per page; a dead feed leaves the
  // block hidden (state 'empty') rather than a heading over nothing.
  var PICKS_CAP = 4;
  function picksQuery(aud) {
    return 'available_for_sale:true AND vendor:"EBC Brand" AND tag:ebc-' + aud;
  }
  function makePick(node, MODEL, money) {
    var cp = MODEL.productFromStorefrontNode(node);
    var dp = MODEL.displayPrice(cp);
    var li = document.createElement('li');
    var a = document.createElement('a');
    a.className = 'km2-mega__pick';
    a.setAttribute('href', '/products/' + encodeURIComponent(node.handle || ''));
    var img = document.createElement('img');
    img.setAttribute('src', MODEL.imageUrl((node.featuredImage && node.featuredImage.url) || '', 300));
    img.setAttribute('alt', '');
    img.setAttribute('loading', 'lazy');
    img.setAttribute('width', '300');
    img.setAttribute('height', '300');
    var name = document.createElement('span');
    name.className = 'km2-mega__pick-name';
    name.textContent = node.title || ''; // textContent: XSS-safe like the brand lists
    a.appendChild(img);
    a.appendChild(name);
    if (dp && dp.price !== null && money) {
      var price = document.createElement('span');
      price.className = 'km2-mega__pick-price';
      var shown = money(dp.price);
      if (dp.from) {
        var tpl = (window.variantStrings && window.variantStrings.fromPrice) || 'From [price]'; // same wording as the cards
        shown = tpl.indexOf('[price]') !== -1 ? tpl.replace('[price]', shown) : tpl + ' ' + shown;
      }
      price.textContent = shown;
      a.appendChild(price);
    }
    li.appendChild(a);
    return li;
  }
  function fillPicks(panelEl, aud) {
    var box = panelEl.querySelector('[data-km2-ebc-picks="' + aud + '"]');
    if (!box || box.getAttribute('data-km2-ebc-picks-state')) return;
    var list = box.querySelector('[data-km2-ebc-picks-list]');
    var KB = window.KM2Brands;
    var MODEL = window.KM2ProductModel;
    var CARD = window.KM2ProductCard;
    if (!list || !KB || !KB.createProductPager || !KB.config || !KB.config.token || !MODEL) return; // retried on next open
    var side = box.closest('[data-km2-ebc-side]');
    var money = CARD && CARD.create ? CARD.create({ moneyFormat: side && side.getAttribute('data-km2-money-format') }).money : null;
    box.setAttribute('data-km2-ebc-picks-state', 'pending');
    KB.createProductPager({ query: picksQuery(aud), sortKey: 'BEST_SELLING', pageSize: PICKS_CAP })
      .nextPage()
      .then(function (page) {
        var nodes = ((page && page.nodes) || []).slice(0, PICKS_CAP);
        nodes.forEach(function (node) { list.appendChild(makePick(node, MODEL, money)); });
        box.setAttribute('data-km2-ebc-picks-state', nodes.length ? 'done' : 'empty');
      })
      .catch(function () {
        box.setAttribute('data-km2-ebc-picks-state', 'empty');
      });
  }

  function selectFirstVisibleTab(panelEl) {
    var tabs = panelEl.querySelectorAll('[data-km2-tab]');
    if (!tabs.length) return;
    var current = panelEl.querySelector('[data-km2-tab][aria-selected="true"]');
    if (current && !current.hidden) return;
    for (var i = 0; i < tabs.length; i++) {
      if (!tabs[i].hidden) {
        selectTab(panelEl, tabs[i], false);
        return;
      }
    }
  }

  function bindTabs(root) {
    var lists = (root || document).querySelectorAll('[data-km2-panel] [data-km2-tablist]');
    Array.prototype.forEach.call(lists, function (list) {
      if (list.getAttribute('data-km2-tabs-bound') === '1') return;
      list.setAttribute('data-km2-tabs-bound', '1');
      var panelEl = list.closest('[data-km2-panel]');
      var visibleTabs = function () {
        return Array.prototype.filter.call(list.querySelectorAll('[data-km2-tab]'), function (t) {
          return !t.hidden;
        });
      };
      Array.prototype.forEach.call(list.querySelectorAll('[data-km2-tab]'), function (tab) {
        // Hover, focus and click all show the tab's pane (mockup B: "hover a tab on the left").
        tab.addEventListener('mouseenter', function () { selectTab(panelEl, tab, false); });
        tab.addEventListener('focus', function () { selectTab(panelEl, tab, false); });
        tab.addEventListener('click', function () { selectTab(panelEl, tab, false); });
      });
      list.addEventListener('keydown', function (e) {
        var tabs = visibleTabs();
        var idx = tabs.indexOf(document.activeElement);
        if (idx < 0) return;
        var next = null;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = tabs[(idx + 1) % tabs.length];
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = tabs[(idx - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        selectTab(panelEl, next, true);
      });
    });
  }

  function smartHideCategories(panelEl) {
    if (!panelEl || panelEl.getAttribute('data-km2-cat-hidden')) return; // once per panel
    if (!window.KM2Brands || !window.KM2Brands.config || !window.KM2Brands.config.token) return;
    var links = panelEl.querySelectorAll('[data-km2-collection]');
    if (!links.length) return;
    var handles = [];
    var seen = Object.create(null);
    for (var i = 0; i < links.length; i++) {
      var h = links[i].getAttribute('data-km2-collection');
      if (h && !seen[h]) {
        seen[h] = true;
        handles.push(h);
      }
    }
    panelEl.setAttribute('data-km2-cat-hidden', 'pending');
    var cacheKey = AVAIL_PREFIX + (panelEl.getAttribute('data-km2-panel') || panelEl.getAttribute('data-km2-drawer-panel') || 'default');
    var cached = null;
    try {
      var raw = sessionStorage.getItem(cacheKey);
      cached = raw ? JSON.parse(raw) : null;
    } catch (e) {
      /* ignore */
    }
    // Stale-while-revalidate: paint instantly from cache (no flicker on re-open), but ALWAYS re-fetch
    // and re-apply so a stale verdict self-corrects. The old code returned here and never refreshed,
    // so a verdict cached early in a session (e.g. before a collection was published / restocked) stuck
    // for the whole session — the exact "category missing for minutes" bug.
    if (cached && cached.map) {
      applyHide(panelEl, cached.map);
    }
    fetchAvailability(handles)
      .then(function (map) {
        if (!map) {
          if (!cached) panelEl.removeAttribute('data-km2-cat-hidden'); // fail-open only if nothing painted
          return;
        }
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ map: map, ts: Date.now() }));
        } catch (e) {
          /* ignore */
        }
        applyHide(panelEl, map); // fresh truth — un-hides restocked categories, hides sold-out ones
        panelEl.setAttribute('data-km2-cat-hidden', 'done');
      })
      .catch(function () {
        panelEl.removeAttribute('data-km2-cat-hidden'); // fail-open
      });
  }

  function hydrate(brandEl) {
    if (!brandEl || brandEl.getAttribute('data-km2-hydrated')) return; // once per panel
    var listEl = brandEl.querySelector('[data-km2-brand-list]');
    if (!listEl) return;
    var context = brandEl.getAttribute('data-km2-context') || 'default';
    var query = brandEl.getAttribute('data-km2-query') || '';
    var client = window.KM2Brands;
    if (!query || !client || !client.config || !client.config.token) return; // not configured
    brandEl.setAttribute('data-km2-hydrated', 'pending');

    client
      .fetchTopBrands({ key: context, query: query, cap: 0, denyList: denyFor(context) })
      .then(function (brands) {
        reconcile(listEl, brands || []);
        brandEl.setAttribute('data-km2-hydrated', 'done');
      })
      .catch(function () {
        brandEl.removeAttribute('data-km2-hydrated'); // allow retry on next open
      });
  }

  // ---- Task 5: Brands two-tier directory hydration ----
  // The Brands panel ([data-km2-brands-dir]) renders Featured houses (curated, server-rendered)
  // + an empty "More brands A–Z" list. On open we fetch EVERY in-stock vendor (deny-list OFF —
  // unlike the audience panels, the store house label belongs in a full directory), cull any OOS
  // featured hero, then fill More with the non-featured vendors sorted A–Z (textContent, XSS-safe).
  function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild); // avoids innerHTML (XSS-safe + grep-clean)
  }

  // Reconcile ONE curated, server-rendered pinned tier (Featured / future curated
  // tiers) against live stock: drop heroes that are fully OOS, upgrade each survivor's /search
  // fallback to the canonical /collections/<slug>, sort A–Z, and record every surviving vendor in
  // `exclude` so the More A–Z tier never repeats it. One routine for every curated tier → a new
  // tier needs zero new cull logic (DRY + Open/Closed).
  function reconcileCuratedTier(listEl, inStock, exclude) {
    if (!listEl) return;
    var pins = listEl.querySelectorAll('[data-km2-pinned]');
    for (var i = 0; i < pins.length; i++) {
      var nm = (pins[i].getAttribute('data-km2-pinned') || '').trim();
      if (!nm) continue;
      if (inStock[nm.toLowerCase()]) {
        exclude[nm.toLowerCase()] = true;
        var link = pins[i].querySelector('a');
        if (link) link.setAttribute('href', brandHref(nm));
      } else if (pins[i].parentNode) {
        pins[i].parentNode.removeChild(pins[i]); // sold-out = absent, not struck-through
      }
    }
    sortBrandList(listEl); // curated tiers listed A–Z (more-tier is sorted separately below)
  }

  // Featured brands fill ONE column-fill flow (km2-mega__brand-flow). Pick 2 columns normally,
  // 3 when the surviving items would overflow the 2-column height budget. Count-based (no fragile
  // DOM measurement): FLOW_ITEMS_PER_COL approximates the fixed flow height.
  // Clamp [2,3]; the ≤749px media query forces 1 column with !important.
  var FLOW_ITEMS_PER_COL = 5;   // ~16rem column height ÷ one brand line -> 12 featured = 3 cols
  var FLOW_COL_WIDTH_REM = 13;  // widest house (Yves Saint Laurent) fits one column
  function setBrandFlowColumns(dirEl) {
    if (!dirEl) return;
    var flow = dirEl.querySelector('[data-km2-brand-flow]');
    if (!flow) return;
    var featured = flow.querySelectorAll('[data-km2-featured-list] > .km2-mega__brand').length;
    var cols = Math.max(2, Math.min(3, Math.ceil(featured / FLOW_ITEMS_PER_COL)));
    // Only drive desktop; the ≤749px media query (column-count:1 !important) overrides this inline.
    flow.style.columnCount = String(cols);
    flow.style.width = (cols * FLOW_COL_WIDTH_REM + (cols - 1) * 1.5) + 'rem';
  }

  /*
   * Settle the "More brands A-Z" tier into a DEFINITE state.
   *
   * The server ships a `Loading brands...` <li> placeholder, and only the SUCCESS path used to
   * clear it. Every other exit — the fetch rejecting, or the client not being configured — left
   * that placeholder in the DOM with nothing in flight behind it, so a shopper saw a spinner that
   * could never resolve (independent crawl, 2026-09-07: a dead Storefront feed stranded it
   * permanently, with no error and no retry affordance).
   *
   * "Loading" must describe a request that is actually running. Route EVERY exit path through
   * here so that invariant cannot be broken again by a future branch: the placeholder is always
   * dropped, and an empty tier hides its own heading (no orphan header).
   */
  function settleMoreTier(dirEl, vendors, opts) {
    var moreList = dirEl && dirEl.querySelector('[data-km2-more-list]');
    if (!moreList) return;
    clearChildren(moreList); // the placeholder never outlives the request it described
    (vendors || []).forEach(function (b) {
      moreList.appendChild(makeBrandLi(String(b.vendor)));
    });
    var tier = moreList.closest('[data-km2-tier="more"]');
    if (!tier) return;
    if ((vendors || []).length) { tier.hidden = false; return; }

    // Empty. The sibling tiers survive a dead feed because their curated names are rendered by
    // Liquid; this tier has NO server-rendered list — it is filled entirely from the feed — so
    // "fall back to the server-rendered list" (owner sign-off finding-4) has nothing to fall back
    // to literally. The nearest honest equivalent is to keep the tier and offer the one path that
    // still works, instead of deleting a whole column out from under the shopper.
    if (opts && opts.fallback) {
      // The phone/tablet drawer copy is rooted on data-km2-drawer-panel; it needs the same path.
      var panel = dirEl.closest('[data-km2-panel], [data-km2-drawer-panel]') || dirEl;
      var shopAll = panel.querySelector('[data-km2-shop-all]');
      var href = shopAll && shopAll.getAttribute('href');
      if (href) {
        var li = document.createElement('li');
        li.className = 'km2-mega__brand';
        var a = document.createElement('a');
        a.setAttribute('href', href);
        a.textContent = 'Browse all brands \u2192'; // textContent: same XSS posture as makeBrandLi
        li.appendChild(a);
        moreList.appendChild(li);
        tier.hidden = false;
        return;
      }
    }
    tier.hidden = true; // nothing to show and no path to offer — no orphan header
  }

  function hydrateBrandsDir(dirEl) {
    if (!dirEl || dirEl.getAttribute('data-km2-hydrated')) return; // once per panel
    var moreList = dirEl.querySelector('[data-km2-more-list]');
    if (!moreList) return;
    var featuredList = dirEl.querySelector('[data-km2-featured-list]');
    var context = dirEl.getAttribute('data-km2-context') || 'brands';
    var query = dirEl.getAttribute('data-km2-query') || '';
    // "not yet" is NOT "never" — the distinction this branch originally collapsed, in both
    // directions. A missing query means the section is genuinely misconfigured: nothing will ever
    // arrive, so settle the tier now. A missing KM2Brands means only that its DEFERRED asset has
    // not executed yet; treating that as definitive hid the whole "More brands A-Z" tier on a
    // load-order race, which is exactly what a panel opened immediately after page load hit.
    // Wait for the dependency instead of concluding it is absent (KM2Deps.whenAvailable), and
    // settle empty only once it genuinely times out.
    if (!query) {
      settleMoreTier(dirEl, []); // misconfigured — nothing can ever be in flight
      return;
    }
    var client = window.KM2Brands;
    var clientReady = (client && client.config && client.config.token)
      ? Promise.resolve(client)
      : (window.KM2Deps && window.KM2Deps.whenAvailable
          ? window.KM2Deps.whenAvailable('KM2Brands', 'fetchTopBrands', 'mega-menu brands directory')
          : Promise.resolve(null));

    dirEl.setAttribute('data-km2-hydrated', 'pending'); // blocks a duplicate concurrent hydration

    clientReady
      .then(function (c) {
        if (!c || !c.config || !c.config.token) {
          // The dependency never arrived. NOW it is definitive: drop the placeholder so no
          // shopper waits on a request that does not exist, and stay retry-eligible.
          settleMoreTier(dirEl, [], { fallback: true });
          dirEl.removeAttribute('data-km2-hydrated');
          return null;
        }
        // Only EBC is denied here: the store house label still belongs in a full A–Z directory,
        // but EBC has its own menu (D7) and must not appear in Featured or More.
        return c.fetchTopBrands({ key: context, query: query, cap: 0, denyList: EBC_VENDORS.slice() });
      })
      .then(function (brands) {
        if (brands === null) return; // dependency-timeout path already settled above
        brands = brands || [];
        var inStock = Object.create(null);
        brands.forEach(function (b) {
          inStock[String(b.vendor).toLowerCase()] = true;
        });

        // 1) Reconcile the curated Featured tier against live stock. Each surviving vendor is
        //    culled-if-OOS, href-upgraded, sorted, and recorded in `exclude`.
        var exclude = Object.create(null);
        reconcileCuratedTier(featuredList, inStock, exclude);
        setBrandFlowColumns(dirEl); // re-shape the flow after culling (2↔3 cols)

        // 2) More tier: every in-stock vendor NOT in a curated tier, sorted A–Z.
        var more = brands.filter(function (b) {
          return !exclude[String(b.vendor).toLowerCase()];
        });
        more.sort(function (a, b) {
          return String(a.vendor).localeCompare(String(b.vendor));
        });
        settleMoreTier(dirEl, more);
        dirEl.setAttribute('data-km2-hydrated', 'done');
      })
      .catch(function () {
        settleMoreTier(dirEl, [], { fallback: true }); // dead feed: offer a path, not a void
        dirEl.removeAttribute('data-km2-hydrated'); // still retry-eligible on next open
      });
  }

  function bind(root) {
    var scope = root || document;
    var brandEls = scope.querySelectorAll('[data-km2-brands]');
    Array.prototype.forEach.call(brandEls, function (brandEl) {
      var panel = brandEl.closest('[data-km2-panel]');
      var parent =
        brandEl.closest('li.mega_link') ||
        brandEl.closest('details') ||
        panel ||
        brandEl;
      var trigger = function () {
        hydrate(brandEl);
      };
      parent.addEventListener('mouseenter', trigger);
      parent.addEventListener('focusin', trigger);
      var details = parent.tagName === 'DETAILS' ? parent : parent.querySelector('details');
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) trigger();
        });
      }
    });

    // PD6: smart-hide binds on every panel that holds category links — audience panels, the EBC
    // panel (no Top Brands column) and drawer panels alike — on the same open triggers.
    var catPanels = scope.querySelectorAll('[data-km2-panel], [data-km2-drawer-panel]');
    Array.prototype.forEach.call(catPanels, function (panel) {
      if (!panel.querySelector('[data-km2-collection]')) return;
      var parent = panel.closest('li.mega_link') || panel.closest('details') || panel;
      var trigger = function () {
        smartHideCategories(panel);
        var sel = panel.querySelector('[data-km2-tab][aria-selected="true"]');
        if (sel) fillPicks(panel, sel.getAttribute('data-km2-tab')); // EBC bestsellers for the open tab
      };
      parent.addEventListener('mouseenter', trigger);
      parent.addEventListener('focusin', trigger);
      var details = parent.tagName === 'DETAILS' ? parent : parent.querySelector('details');
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) trigger();
        });
      }
    });

    // Task 5: the Brands two-tier directory hydrates via the same open triggers (hover/focus/toggle).
    var dirEls = scope.querySelectorAll('[data-km2-brands-dir]');
    Array.prototype.forEach.call(dirEls, function (dirEl) {
      var parent = dirEl.closest('li.mega_link') || dirEl.closest('details') || dirEl;
      setBrandFlowColumns(dirEl); // optimistic first paint: shape the server-rendered Featured list
      var trigger = function () {
        hydrateBrandsDir(dirEl);
      };
      parent.addEventListener('mouseenter', trigger);
      parent.addEventListener('focusin', trigger);
      var details = parent.tagName === 'DETAILS' ? parent : parent.querySelector('details');
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) trigger();
        });
      }
    });
  }

  // ---- Task 4: interaction model — desktop hover-open + click-navigate; touch two-tap ----
  // Scoped to the audience panels ([data-km2-panel]); other mega items keep the legacy toggle.
  // Hover-open itself is the theme's CSS :hover; this only governs what a CLICK/TAP does.
  function bindInteraction(root) {
    var panels = (root || document).querySelectorAll('[data-km2-panel]');
    Array.prototype.forEach.call(panels, function (panel) {
      var li = panel.closest('li.mega_link');
      var summary = li && li.querySelector('summary');
      var shopAll = panel.querySelector('[data-km2-shop-all]');
      var url = shopAll && shopAll.getAttribute('href');
      if (!li || !summary || !url || summary.getAttribute('data-km2-nav') === '1') return;
      summary.setAttribute('data-km2-nav', '1'); // bind once per summary
      var details = li.querySelector('details');

      // Touch two-tap state is tracked EXPLICITLY rather than inferred from details.open.
      // Inferring it was wrong: by the time this click listener runs, something upstream (the
      // focus/pointer path that reveals the panel) has already set open=true, so the FIRST tap
      // looked like a second tap and navigated away — the panel could never be opened by touch
      // at all (independent crawl, 2026-09-07: one tap on "Men" went straight to
      // /collections/men). A flag we set ourselves cannot be desynchronised by whoever else
      // toggles the panel.
      var tapped = false;
      if (details) {
        // Whenever the panel actually closes, the next tap is a FIRST tap again.
        details.addEventListener('toggle', function () {
          if (!details.open) tapped = false;
        });
      }

      summary.addEventListener('click', function (e) {
        if (e.detail === 0) return; // keyboard (Enter/Space) -> keep native open/close for a11y
        var hoverCapable = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (hoverCapable) {
          // Desktop: hover already revealed the panel, so a click goes to the collection.
          e.preventDefault();
          window.location.href = url;
          return;
        }
        if (!tapped) {
          // FIRST tap: reveal the panel and stay put, whatever open already says.
          tapped = true;
          if (details && !details.open) details.open = true;
          e.preventDefault(); // never navigate on the opening tap
          return;
        }
        // SECOND tap on an open panel -> navigate.
        e.preventDefault();
        window.location.href = url;
      });
    });
  }

  /**
   * Escape-to-close, SCOPED TO THE CARE PANEL ONLY (plan 20260820-170108, AC5c).
   *
   * Honest note: no Escape handler exists anywhere in this nav today — not in
   * details-disclosure.js, global.js or this file. Adding it here makes the care
   * panel better-behaved than the four audience panels. Bringing the rest of the
   * nav up to match is a deliberate SEPARATE follow-up, not silently widened scope
   * — so this deliberately does not bind to [data-km2-panel] generally.
   *
   * On Escape the panel closes and focus returns to its <summary>, so a keyboard
   * user is never stranded inside a closed disclosure.
   */
  function bindCareEscape(root) {
    var doc = root || document;
    if (doc.__km2CareEscapeBound) return;
    doc.__km2CareEscapeBound = true;

    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;

      var panel = doc.querySelector('[data-km2-panel="care"]');
      if (!panel) return;

      var li = panel.closest('li.mega_link');
      var details = li && li.querySelector('details');
      if (!details || !details.open) return;

      details.open = false;
      var summary = li.querySelector('summary');
      if (summary && typeof summary.focus === 'function') summary.focus();
    });
  }

  function init() {
    function run() {
      bind(document);
      bindTabs(document);
      bindInteraction(document);
      bindCareEscape(document);
    }
    // KM2Deps.ready: 'interactive' means parsed but DEFERRED SCRIPTS NOT YET RUN, so the old
    // `else run()` fired before the shared globals existed. Only 'complete' runs immediately.
    (window.KM2Deps ? window.KM2Deps.ready(run)
      : (document.readyState === 'complete' ? run() : document.addEventListener('DOMContentLoaded', run, { once: true })));
  }

  // Exposed for tests + future tasks (Task 4 interaction model layers onto bind()).
  window.KM2MegaMenu = {
    hydrate: hydrate,
    hydrateBrandsDir: hydrateBrandsDir,
    reconcile: reconcile,
    bind: bind,
    bindInteraction: bindInteraction,
    bindTabs: bindTabs,
    selectFirstVisibleTab: selectFirstVisibleTab,
    smartHideCategories: smartHideCategories,
    setBrandFlowColumns: setBrandFlowColumns, // exposed for tests / re-shape after manual changes
  };
  init();
})();
